import React, { useState } from 'react';
import EnhancedTranscript from './EnhancedTranscript';

/**
 * AllRecordings - Shared component to display all recording sessions and segments
 * Can be used across different recording contexts (Zoom, In-Person, Twilio, etc.)
 */
const AllRecordings = ({
    existingSessions = [],
    audioSegments = [],
    activeSessionDetails = null,
    loadingSessions = false,
    onViewNote = null, // Callback when user wants to view a note
    onClose = null, // Callback when user wants to close the modal
    formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
}) => {
    const [isClosing, setIsClosing] = useState(false);
    

    const handleClose = () => {
        setIsClosing(true);
        // Call onClose callback after animation completes
        setTimeout(() => {
            if (onClose) {
                onClose();
            }
        }, 300); // Match animation duration
    };

    return (
        <div className={`card ${isClosing ? 'slide-out' : ''}`}>
            <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">All Recordings</h5>
                <button 
                    type="button" 
                    className="btn-close" 
                    aria-label="Close"
                    onClick={handleClose}
                ></button>
            </div>
            <div className="card-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {loadingSessions && (
                <div className="d-flex justify-content-center align-items-center py-4">
                    <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <span className="text-muted">Loading existing sessions...</span>
                </div>
            )}

            {/* Active Session Details */}
            {activeSessionDetails && (
                <>
                <div className="d-flex align-items-center mb-2">
                    <h6 className="alert-heading mb-0">Active Recording Session</h6>
                </div>
                <div className="alert alert-info mb-3">
                    <div className="row g-2 mb-2">
                        <div className="col-auto">
                            <strong>Session:</strong> {activeSessionDetails.session_id.slice(0, 8)}...
                        </div>
                        <div className="col-auto">
                            <span className="badge bg-primary">{activeSessionDetails.status}</span>
                        </div>
                    </div>
                    <div className="mb-2">
                        <strong>Segments:</strong> {activeSessionDetails.segment_count} recorded
                        {activeSessionDetails.segments?.length > 0 && (
                            <small className="text-muted ms-2">
                                ({activeSessionDetails.segments.filter(s => s.transcription_status === 'completed').length} transcribed)
                            </small>
                        )}
                    </div>

                    {/* Active session segments */}
                    {activeSessionDetails.segments?.length > 0 && (
                        <div className="mt-3">
                            <div className="list-group list-group-flush">
                                {activeSessionDetails.segments.map((segment, index) => (
                                    <div key={index} className="list-group-item px-0">
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div className="flex-grow-1">
                                                <h6 className="mb-1">Segment {segment.segment_number}</h6>
                                                <small className="text-muted">
                                                    Duration: {formatDuration(segment.duration_seconds)} •
                                                    Size: {segment.file_size_bytes ? (segment.file_size_bytes / 1024 / 1024).toFixed(2) + ' MB' : 'N/A'}
                                                    {segment.source && ` • Source: ${segment.source}`}
                                                </small>
                                            </div>
                                            <span className={`badge ${
                                                segment.transcription_status === 'completed' ? 'bg-success' :
                                                segment.transcription_status === 'failed' ? 'bg-danger' :
                                                segment.transcription_status === 'processing' ? 'bg-warning text-dark' : 'bg-secondary'
                                            }`}>
                                                {segment.transcription_status === 'completed' ? 'Transcribed' :
                                                    segment.transcription_status === 'failed' ? 'Failed' :
                                                    segment.transcription_status === 'processing' ? 'Processing' :
                                                    segment.s3_url ? 'Uploaded' : 'Pending'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Enhanced transcript with speaker diarization (if available) */}
                    {activeSessionDetails.session_transcript?.utterances && activeSessionDetails.session_transcript.utterances.length > 0 && (
                        <div className="mt-3">
                            <EnhancedTranscript
                                utterances={activeSessionDetails.session_transcript.utterances}
                                detectedLanguage={activeSessionDetails.session_transcript.detected_language}
                                languageConfidence={activeSessionDetails.session_transcript.language_confidence}
                                speakerLabelsMap={activeSessionDetails.session_transcript.speaker_labels_map || {}}
                                onRename={null}
                            />
                        </div>
                    )}
                </div>
                </>
            )}

            {/* Current Session Segments (from hook) */}
            {audioSegments.length > 0 && !activeSessionDetails && (
                <div className="mb-3">
                    <h6 className="mb-2">Current Session ({audioSegments.length} segments)</h6>
                    <div className="list-group">
                        {audioSegments.map((segment, index) => (
                            <div key={index} className="list-group-item">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                        <h6 className="mb-1">Segment {segment.segmentNumber}</h6>
                                        <small className="text-muted">
                                            Duration: {formatDuration(segment.duration)} • 
                                            Size: {(segment.fileSize / 1024 / 1024).toFixed(2)} MB
                                        </small>
                                    </div>
                                    <span className="badge bg-success">
                                        <i className="bi bi-cloud-upload-fill me-1"></i>
                                        Uploaded
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Previous Sessions */}
            {existingSessions.length > 0 && (
                <div className="mb-3">
                    <h6 className="mb-2">Previous Sessions ({existingSessions.length})</h6>
                    <div className="list-group">
                        {existingSessions.map((session) => (
                            <div key={session.session_id} className="list-group-item">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                        <h6 className="mb-1">Session {session.session_id.slice(0, 8)}...</h6>
                                        <small className="text-muted d-block">
                                            {session.segment_count} segments • 
                                            {new Date(session.created_at).toLocaleDateString()}
                                            {session.completed_at && ` - ${new Date(session.completed_at).toLocaleDateString()}`}
                                        </small>
                                    </div>
                                    <div className="text-end">
                                        <div className="mb-2">
                                            <span className={`badge ${
                                                session.status === 'completed' ? 'bg-success' :
                                                session.status === 'active' ? 'bg-primary' : 'bg-secondary'
                                            }`}>
                                                {session.status}
                                            </span>
                                        </div>
                                        {session.visit_note_id && (
                                            <button 
                                                type="button"
                                                className="btn btn-sm btn-outline-info"
                                                onClick={() => onViewNote && onViewNote(session)}
                                                title="Click to view visit note"
                                            >
                                                <i className="bi bi-file-medical me-1"></i>
                                                View Note {session.note_version && `(v${session.note_version})`}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Empty state */}
            {!loadingSessions && existingSessions.length === 0 && audioSegments.length === 0 && !activeSessionDetails && (
                <div className="text-center py-4">
                    <i className="bi bi-mic text-muted" style={{ fontSize: '2rem' }}></i>
                    <p className="text-muted mt-2 mb-0">No recordings found for this case</p>
                    <small className="text-muted">Start a recording session to see data here</small>
                </div>
            )}
            </div>
            
            {/* Footer with summary */}
            <div className="card-footer bg-light">
                <div className="d-flex justify-content-between align-items-center">
                    <div className="text-muted small">
                        {activeSessionDetails && (
                            <span className="me-3">
                                <i className="bi bi-record-circle text-danger me-1"></i>
                                Active Recording
                            </span>
                        )}
                        <span>
                            Total Sessions: {existingSessions.length + (activeSessionDetails ? 1 : 0)}
                        </span>
                        {audioSegments.length > 0 && !activeSessionDetails && (
                            <span className="ms-3">
                                Current Segments: {audioSegments.length}
                            </span>
                        )}
                    </div>
                    <button 
                        type="button" 
                        className="btn btn-sm btn-secondary" 
                        onClick={handleClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AllRecordings;